import { FileSourceEnum } from '@systemeio/file-manager'
import { Checkbox, FormInput, FormSelect, Hint } from '@systemeio/ui'
import ItemSteps, { ItemStepsProps } from 'modules/stores/store/components/item-steps'
import { StoreTextEditor } from 'modules/stores/store/components/store-text-editor'
import { ReactNode, useEffect } from 'react'
import CodeEditor from 'shared/components/code-editor'
import ConfirmationModal from 'shared/components/confirmation-modal'
import { ImageUploaderWithPreviewImage } from 'shared/components/file-uploader/image-uploader-with-preview-image'
import FormUrlPathInput from 'shared/components/form-url-path-input'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUserDomains from 'shared/hooks/use-user-domains'
import { getFormSelectData } from 'shared/utils/get-form-select-data'
import { twJoin, twMerge } from 'tailwind-merge'
import { useCourse } from '../../../hooks/use-course'
import { CourseId } from '../../../types/course-interface'
import { useSaveOnBlur } from './use-save-on-blur'
import { useSaveOnSubmit } from './use-save-on-submit'

interface Props extends Pick<ItemStepsProps, 'hideNumbers' | 'useSectionHeading'> {
  courseId?: CourseId
  showDomain?: boolean
  showNameAndDescription?: boolean
  saveOnBlur?: boolean
  additionalItems?: { key: string; title: ReactNode; element: ReactNode }[]
  onStatusChange?: (_: { isFetching: boolean; disabled: boolean }) => void
}

export const LightCourseSettings = ({
  courseId,
  showDomain = false,
  showNameAndDescription = false,
  hideNumbers,
  useSectionHeading,
  saveOnBlur = true,
  additionalItems,
  onStatusChange,
}: Props) => {
  const { t } = useLocoTranslation()
  const { courseData, isLoading } = useCourse(courseId)
  const { userDomains } = useUserDomains()

  const onBlurSaver = useSaveOnBlur({ courseId })
  const onSubmitSaver = useSaveOnSubmit({ courseId })

  const {
    tempState,
    setTempState,
    domainConfirmationModal,
    errors,
    isSuccess,
    statusIcon,
    handleBlur,
    isInProgress,
    isFetching,
    handleTeacherPhotoChange,
    handleCourseLogoChange,
    handleCourseCoverChange,
    handleCertificateLogoChange,
    handleSignatureChange,
    handleDomainChange,
    handleDomainChangeConfirm,
    handleIssueCertificateUponCompletionChange,

    handleFormSubmit,

    isCodeValid,
    setIsCodeValid,
  } = saveOnBlur
    ? { ...onBlurSaver, handleFormSubmit: undefined, isFetching: undefined }
    : {
        ...onSubmitSaver,
        isSuccess: undefined,
        statusIcon: undefined,
        handleBlur: undefined,
        isInProgress: undefined,
      }

  useEffect(() => {
    if (onStatusChange) {
      onStatusChange({ isFetching: isFetching ?? false, disabled: !isCodeValid })
    }
  }, [isFetching, isCodeValid])

  const fields = [
    {
      key: 'teacher',
      title: t('dashboard.light_course.instructor_info'),
      element: (
        <div className="flex flex-col gap-5 sm:flex-row">
          <FormInput
            className="w-full"
            label={t('dashboard.light_course.instructor_name')}
            value={tempState?.instructorName ?? ''}
            placeholder={t('dashboard.light_course.instructor_name')}
            onChange={e =>
              setTempState(prev => ({
                ...prev,
                instructorName: e.target.value ?? '',
              }))
            }
            onBlur={e => handleBlur?.instructorName(e.target.value)}
            isPreFetching={isLoading}
            disabled={!courseData}
            error={errors?.instructorName}
            success={isSuccess?.instructorName}
            statusIcon={statusIcon?.instructorName}
          />

          <ImageUploaderWithPreviewImage
            className="w-full"
            previewClassName="h-[200px] w-[200px]"
            source={FileSourceEnum.Membership}
            aspectRatio={1}
            label={
              <Hint label={t('dashboard.settings.membership.teacher_photo_popover_title')}>
                {t('dashboard.settings.membership.teacher_photo_title')}
              </Hint>
            }
            value={tempState?.teacherPhoto}
            onRemove={() => {
              handleTeacherPhotoChange()
            }}
            onSelect={handleTeacherPhotoChange}
            isPreFetching={isLoading}
            disabled={!courseData || isInProgress?.teacherPhoto}
            error={errors?.teacherPhoto}
            isSuccess={isSuccess?.teacherPhoto}
            statusIcon={statusIcon?.teacherPhoto}
          />
        </div>
      ),
    },
    {
      key: 'course',
      title: t('dashboard.light_course.branding_settings'),
      element: (
        <>
          {showDomain && (
            <div className="flex flex-col flex-wrap gap-5 sm:flex-row">
              <FormSelect
                data={getFormSelectData(userDomains || [])}
                label={t('dashboard.course.course_domain_title')}
                className="sm:min-w-0"
                containerClassName="min-w-[200px] flex-1"
                required
                value={tempState?.domainId || undefined}
                onChange={handleDomainChange}
                isPreFetching={isLoading}
                disabled={!courseData}
                withoutCloseIcon
              />

              <ConfirmationModal
                title={t('dashboard.course.course_domain_confirm_title')}
                confirmationText={t('dashboard.course.course_domain_confirmation_text_title')}
                opened={domainConfirmationModal.opened}
                toastCaption={saveOnBlur && t('global.changes_saved')}
                onClose={domainConfirmationModal.close}
                onConfirm={handleDomainChangeConfirm}
              />

              <FormUrlPathInput
                wrapperClassName="flex-1"
                value={tempState?.path}
                onChange={e => setTempState(prev => ({ ...prev, path: e.target.value }))}
                onBlur={e => handleBlur?.path(e.target.value)}
                label={
                  <>
                    <div className="flex items-center gap-1.5">
                      {t('global.url_path')}
                      <Hint
                        label={t('dashboard.funnel.configuration.webinar.url_path_popover-2')}
                      />
                    </div>
                  </>
                }
                required
                domainName={userDomains?.find(el => el.id === tempState?.domainId)?.name}
                additionalPath="school/course"
                isPreFetching={isLoading}
                disabled={!courseData || isInProgress?.path}
                error={errors?.path}
                success={isSuccess?.path}
                statusIcon={statusIcon?.path}
                copyButtonClassName={twJoin(
                  isSuccess?.path && '[&>svg]:stroke-background-secondary',
                )}
              />
            </div>
          )}

          {showNameAndDescription && (
            <div className="flex flex-col gap-5">
              <FormInput
                label={t('dashboard.light_course.course_title')}
                required
                value={tempState?.name ?? ''}
                placeholder={t('dashboard.light_course.course_title')}
                onChange={e =>
                  setTempState(prev => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                onBlur={e => handleBlur?.name(e.target.value)}
                isPreFetching={isLoading}
                disabled={!courseData}
                error={errors?.name}
                success={isSuccess?.name}
                statusIcon={statusIcon?.name}
              />

              <StoreTextEditor
                label={t('dashboard.course.description_title')}
                required
                content={tempState?.description ?? ''}
                onChange={value =>
                  setTempState(prev => ({
                    ...prev,
                    description: value,
                  }))
                }
                onBlur={handleBlur?.description}
                isPreFetching={isLoading}
                className={twMerge(
                  isSuccess?.description && 'border-success',
                  errors?.description !== undefined && 'border-danger',
                )}
                error={errors?.description}
                statusIcon={statusIcon?.description}
              />
            </div>
          )}

          <div className="flex flex-col gap-5 xl:flex-row">
            <ImageUploaderWithPreviewImage
              className="w-full"
              previewClassName="w-[364px] h-[200px] !object-contain object-top"
              source={FileSourceEnum.Membership}
              label={
                <Hint label={t('dashboard.course.logo_popover_title')}>
                  {t('dashboard.course.logo_title')}
                </Hint>
              }
              value={tempState?.logo}
              onRemove={() => {
                handleCourseLogoChange()
              }}
              onSelect={handleCourseLogoChange}
              isPreFetching={isLoading}
              disabled={!courseData || isInProgress?.logo}
              error={errors?.logo}
              isSuccess={isSuccess?.logo}
              statusIcon={statusIcon?.logo}
            />

            <ImageUploaderWithPreviewImage
              className="w-full"
              previewClassName="h-[200px] w-[364px]"
              label={
                <Hint label={t('dashboard.course.image_popover_title')}>
                  {t('dashboard.course.image_title')}
                </Hint>
              }
              aspectRatio={16 / 9}
              value={tempState?.image}
              source={FileSourceEnum.Membership}
              onRemove={() => {
                handleCourseCoverChange()
              }}
              onSelect={handleCourseCoverChange}
              isPreFetching={isLoading}
              disabled={!courseData || isInProgress?.image}
              error={errors?.image}
              isSuccess={isSuccess?.image}
              statusIcon={statusIcon?.image}
            />
          </div>
        </>
      ),
    },
    {
      key: 'certificate',
      title: t('dashboard.light_course.certificate_settings'),
      element: (
        <div className="flex flex-col gap-5">
          <Checkbox
            label={t('dashboard.course.issue_certificate_upon_completion_title')}
            value={!!tempState?.issueCertificateUponCompletion}
            onChange={handleIssueCertificateUponCompletionChange}
            disabled={!courseData}
            isFetching={isInProgress?.issueCertificateUponCompletion}
            error={errors?.issueCertificateUponCompletion}
            inputWrapperClassName={twMerge(
              isInProgress?.issueCertificateUponCompletion && 'animate-pulse',
            )}
          />
          <div className="flex flex-col gap-5 sm:flex-row">
            <ImageUploaderWithPreviewImage
              className="w-full"
              label={
                <Hint label={t('dashboard.course.certificate_logo_popover_title')}>
                  {t('dashboard.course.certificate_logo_title')}
                </Hint>
              }
              value={tempState?.certificateLogo}
              onRemove={() => {
                handleCertificateLogoChange()
              }}
              onSelect={handleCertificateLogoChange}
              source={FileSourceEnum.Membership}
              isPreFetching={isLoading}
              disabled={!courseData || isInProgress?.certificateLogo}
              error={errors?.certificateLogo}
              isSuccess={isSuccess?.certificateLogo}
              statusIcon={statusIcon?.certificateLogo}
            />

            <ImageUploaderWithPreviewImage
              className="w-full"
              label={
                <Hint label={t('dashboard.course.signature_popover_title')}>
                  {t('dashboard.course.signature_title')}
                </Hint>
              }
              value={tempState?.signature}
              onRemove={() => {
                handleSignatureChange()
              }}
              onSelect={handleSignatureChange}
              source={FileSourceEnum.Membership}
              isPreFetching={isLoading}
              disabled={!courseData || isInProgress?.signature}
              error={errors?.signature}
              isSuccess={isSuccess?.signature}
              statusIcon={statusIcon?.signature}
            />
          </div>
        </div>
      ),
    },
    {
      key: 'code',
      title: t('dashboard.light_course.custom_code'),
      element: (
        <CodeEditor
          value={tempState?.embeddedCode}
          label={
            <Hint label={t('dashboard.course.embedded_code_popover_title')}>
              {t('dashboard.course.embedded_code_title')}
            </Hint>
          }
          onChange={value =>
            setTempState(prev => ({
              ...prev,
              embeddedCode: value,
            }))
          }
          onBlur={handleBlur?.embeddedCode}
          setIsValid={setIsCodeValid}
          disabled={isLoading || !courseData}
          error={errors?.embeddedCode}
          isSuccess={isSuccess?.embeddedCode}
          statusIcon={statusIcon?.embeddedCode}
        />
      ),
    },
    ...(additionalItems ?? []),
  ].filter(Boolean) as {
    key: string
    title: ReactNode
    element: ReactNode
  }[]

  if (saveOnBlur)
    return (
      <ItemSteps steps={fields} hideNumbers={hideNumbers} useSectionHeading={useSectionHeading} />
    )

  return (
    <form
      id={LightCourseSettings.formId}
      onSubmit={e => {
        e.preventDefault()
        handleFormSubmit?.()
      }}
    >
      <ItemSteps steps={fields} hideNumbers={hideNumbers} useSectionHeading={useSectionHeading} />
    </form>
  )
}

LightCourseSettings.formId = 'light-course-settings-form'
